import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role?: string;
  teamId?: number | null;
  teamRole?: string | null;
  teamLogoUrl?: string | null;
  adaEnabled?: boolean;
  // Universe Search access (user 1 + early testers flagged in users.universe_access).
  universeAccess?: boolean;
  // Free trial (MarketMapr; the account holder's for team members): imports
  // add up to importLimit records each, exports need the paid plan.
  trial?: { endsAt: string | null; importLimit: number | null } | null;
  // Set when this is an admin impersonation session — the admin behind the wheel.
  impersonator?: { id: number; email: string; adaEnabled?: boolean } | null;
}

interface Team {
  id: number;
  name: string;
  createdByUserId: number;
  createdAt: string;
  vincereConnected?: boolean;
}

interface Project {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
  created_at: string;
  last_activity_at?: string | null; // when it was last touched (migration 211)
  folder_id?: number | null;
  parent_project_id?: number | null;
  created_by_agent?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  projects: Project[];
  selectedProject: Project | null;
  team: Team | null;
  isLoadingProjects: boolean;
  isLoadingUser: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  logout: () => void;
  stopImpersonating: () => Promise<void>;
  loadProjects: () => Promise<void>;
  selectProject: (project: Project) => void;
  clearSelectedProject: () => void;
  createProject: (name: string, description: string) => Promise<void>;
  deleteProject: (projectId: number) => Promise<void>;
  createTeam: (teamName: string) => Promise<void>;
  loadTeam: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [team, setTeam] = useState<Team | null>(null);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingUser, setIsLoadingUser] = useState(!!localStorage.getItem('token'));

  const API_BASE = '/api';

  useEffect(() => {
    if (token) {
      loadUser();
    }
  }, [token]);

  const loadUser = async () => {
    console.log('[AuthContext] loadUser: Starting...');
    setIsLoadingUser(true);
    try {
      const response = await fetch(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('[AuthContext] loadUser: User loaded successfully');
        setUser(data.user);
        await loadProjects();
        // Load team if user belongs to one
        if (data.user.teamId) {
          try {
            const [teamResponse, vincereResponse] = await Promise.all([
              fetch(`${API_BASE}/teams/${data.user.teamId}`, {
                headers: { Authorization: `Bearer ${token}` }
              }),
              fetch(`${API_BASE}/vincere/status`, {
                headers: { Authorization: `Bearer ${token}` }
              }).catch(() => null),
            ]);
            if (teamResponse.ok) {
              const teamData = await teamResponse.json();
              const vincereData = vincereResponse?.ok ? await vincereResponse.json() : null;
              setTeam({ ...teamData, vincereConnected: vincereData?.connected ?? false });
            }
          } catch (error) {
            console.error('[AuthContext] loadUser: Failed to load team:', error);
          }
        }
      } else {
        console.log('[AuthContext] loadUser: Response not OK, logging out');
        logout();
      }
    } catch (error) {
      console.error('[AuthContext] loadUser: Failed to load user:', error);
      logout();
    } finally {
      console.log('[AuthContext] loadUser: Finished, setting isLoadingUser to false');
      setIsLoadingUser(false);
    }
  };

  const login = async (email: string, password: string) => {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Login failed');
    }

    const data = await response.json();
    setToken(data.token);
    setUser(data.user);
    localStorage.setItem('token', data.token);
    await loadProjects();

    // Notify Tolt of login so referral attribution persists
    // even if the user signed up in a different session
    try { if ((window as any).tolt) (window as any).tolt.signup(email); } catch {}
  };

  const register = async (email: string, password: string, firstName: string, lastName: string) => {
    const response = await fetch(`${API_BASE}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, firstName, lastName })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Registration failed');
    }

    const data = await response.json();
    setToken(data.token);
    setUser(data.user);
    localStorage.setItem('token', data.token);
    await loadProjects();

    // Notify Tolt of signup for referral attribution.
    // If the user arrived via a partner link (e.g. ?ref=keith),
    // Tolt's cookie persists for 90 days, so even if they book
    // a demo first and register weeks later, this connects them
    // to the referring partner.
    try { if ((window as any).tolt) (window as any).tolt.signup(email); } catch {}
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setProjects([]);
    setSelectedProject(null);
    setTeam(null);
    localStorage.removeItem('token');
    localStorage.removeItem('adminEmail');
  };

  // Return an impersonating admin to their own account. Mirrors the impersonation
  // flow (swap the token, hard-reload) so all state re-initialises cleanly.
  const stopImpersonating = async () => {
    const response = await fetch(`${API_BASE}/admin/stop-impersonating`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to return to your account');
    }
    const data = await response.json();
    localStorage.setItem('token', data.token);
    localStorage.removeItem('adminEmail');
    window.location.href = '/';
  };

  const loadProjects = async () => {
    console.log('[AuthContext] loadProjects: Starting...');
    setIsLoadingProjects(true);
    try {
      const response = await fetch(`${API_BASE}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const projectData = await response.json();
        console.log('[AuthContext] loadProjects: Loaded', projectData.length, 'projects');
        setProjects(projectData);
      } else {
        console.log('[AuthContext] loadProjects: Response not OK:', response.status);
      }
    } catch (error) {
      console.error('[AuthContext] loadProjects: Failed to load projects:', error);
    } finally {
      console.log('[AuthContext] loadProjects: Finished, setting isLoadingProjects to false');
      setIsLoadingProjects(false);
    }
  };

  const createProject = async (name: string, description: string) => {
    const response = await fetch(`${API_BASE}/projects`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ name, description })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create project');
    }

    await loadProjects();
  };

  const deleteProject = async (projectId: number) => {
    const response = await fetch(`${API_BASE}/projects/${projectId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete project');
    }

    // If deleted project was selected, clear selection
    if (selectedProject?.id === projectId) {
      setSelectedProject(null);
    }

    await loadProjects();
  };

  const selectProject = (project: Project) => {
    console.log('[AuthContext] selectProject: Selecting project:', project.name);
    setSelectedProject(project);
  };

  const clearSelectedProject = () => {
    console.log('[AuthContext] clearSelectedProject: Clearing selected project');
    setSelectedProject(null);
  };

  const loadTeam = async () => {
    if (!user?.teamId) {
      setTeam(null);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/teams/${user.teamId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const vincereResponse = await fetch(`${API_BASE}/vincere/status`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => null);
        const vincereData = vincereResponse?.ok ? await vincereResponse.json() : null;
        setTeam({ ...data, vincereConnected: vincereData?.connected ?? false });
      } else {
        console.log('[AuthContext] loadTeam: Failed to load team');
        setTeam(null);
      }
    } catch (error) {
      console.error('[AuthContext] loadTeam: Error loading team:', error);
      setTeam(null);
    }
  };

  const createTeam = async (teamName: string) => {
    const response = await fetch(`${API_BASE}/teams`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ name: teamName })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create team');
    }

    const data = await response.json();

    // Update user with new team info
    setUser(prev => prev ? { ...prev, teamId: data.team.id, teamRole: 'admin' } : null);
    setTeam(data.team);

    // Reload projects to get updated team_id assignments
    await loadProjects();
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      projects,
      selectedProject,
      team,
      isLoadingProjects,
      isLoadingUser,
      login,
      register,
      logout,
      stopImpersonating,
      loadProjects,
      selectProject,
      clearSelectedProject,
      createProject,
      deleteProject,
      createTeam,
      loadTeam
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
